import React, { useEffect, useRef } from "react";
import { Num } from "./ui/Num";

/**
 * The retract confirmation.
 *
 * ---------------------------------------------------------------------------
 * RETRACTION IS NOT A RECALL
 * ---------------------------------------------------------------------------
 * Retracting stops everything that has not been handed off yet. It cannot touch
 * what already left: once a push is with FCM or a message is with the mail
 * provider, it is on someone's lock screen or in someone's inbox and no button
 * in this console will take it back.
 *
 * The copy is therefore the entire point of this dialog. An author reaching for
 * Retract is usually reaching for "un-send", and the one thing this screen must
 * never do is let them believe that is what they got. Both halves of the
 * sentence are load-bearing — how many people this saves, and how many it does
 * not — which is why both numbers are named rather than summarised as "some".
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS ARITHMETIC HERE, AND WHY IT IS NOT A VIOLATION
 * ---------------------------------------------------------------------------
 * Everywhere else in Broadcasts, numbers are the server's and we do not do sums
 * on them. That rule exists because reach is a count of PEOPLE and the channels
 * overlap: adding a push audience to an email audience double-counts everyone
 * who gets both, and the server is the only party that knows the overlap.
 *
 * The subtraction below is a different operation. `queued - sent` is two STAGES
 * OF THE SAME FUNNEL for the SAME CHANNEL — the same population measured twice,
 * so the difference is a real quantity and no overlap exists to be
 * double-counted. Channels are summed only after each channel's own difference
 * has been taken, and the results describe messages within their own pipe, not
 * a merged count of humans. That is the distinction: within-channel, between
 * stages, is sound; cross-channel, between audiences, is not.
 *
 * Presentational only — no fetching. `onConfirm` is the owner's problem.
 */

const BUTTON_BASE =
  "inline-flex items-center justify-center rounded-lg px-4 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2";

/** Destructive, not accent. This button ends a send in progress. */
const DANGER_BUTTON = "bg-bc-bad text-white hover:opacity-90";

const GHOST_BUTTON = "text-bc-text-2 hover:bg-bc-subtle";

const DISABLED_BUTTON = "cursor-not-allowed opacity-50";

const TITLE_ID = "retract-broadcast-title";

/**
 * The copy used when the funnel cannot tell us anything.
 *
 * It still refuses to imply a recall. "Anything already sent cannot be
 * recalled" is the floor: we may not know the numbers, but we always know the
 * shape of what retraction does.
 */
const FALLBACK_COPY =
  "This stops delivery to everyone not yet sent to. Anything already sent cannot be recalled.";

/**
 * A finite number, or null. `0` is a number; `null`, `undefined`, `NaN` and
 * `Infinity` are not.
 *
 * @param {unknown} value
 * @returns {number|null}
 */
const asCount = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * Read one stage off one channel's funnel.
 *
 * @param {Object|null|undefined} channel - `funnel.push` or `funnel.email`.
 * @param {string} stageId - A stage id, e.g. "queued" or "sent".
 * @returns {number|null}
 */
const stage = (channel, stageId) => asCount(channel?.[stageId]);

/**
 * Split a funnel into "already gone" and "still stoppable".
 *
 * Returns `null` when there is nothing to compute from — a null funnel, or one
 * whose every relevant stage is missing. A missing number is not a zero, and
 * telling an author "0 people already sent" when we simply do not know would be
 * the same lie as telling them the message can be pulled back.
 *
 * @param {{push: ?Object, email: ?Object}|null|undefined} funnel
 * @returns {{alreadySent: number, notYetSent: number}|null}
 */
const splitRetractCounts = (funnel) => {
  if (!funnel) return null;

  const pushQueued = stage(funnel.push, "queued");
  const pushSent = stage(funnel.push, "sent");
  const emailQueued = stage(funnel.email, "queued");
  const emailSent = stage(funnel.email, "sent");

  const known = [pushQueued, pushSent, emailQueued, emailSent].some(
    (value) => value !== null
  );
  if (!known) return null;

  const alreadySent = (pushSent ?? 0) + (emailSent ?? 0);

  // Within-channel differences first, then a sum of messages — see the header.
  // Floored at 0 because a server that reports more sent than queued (a race
  // between two counters) must not produce a negative headcount on screen.
  const outstanding =
    ((pushQueued ?? 0) - (pushSent ?? 0)) +
    ((emailQueued ?? 0) - (emailSent ?? 0));
  const notYetSent = Math.max(0, outstanding);

  return { alreadySent, notYetSent };
};

/**
 * The retract confirmation modal.
 *
 * @param {Object} props
 * @param {boolean} props.open - Renders nothing when false.
 * @param {{push: ?Object, email: ?Object}|null} [props.funnel] - Per-channel stage counts. Null when unavailable.
 * @param {() => void} [props.onConfirm] - Called when Retract is pressed.
 * @param {() => void} [props.onCancel] - Called by Cancel, Close, Escape and an overlay click.
 * @param {boolean} [props.retracting=false] - A retract is in flight; both buttons disable.
 * @param {string|null} [props.note] - The SERVER's verbatim sentence about what retraction could not undo. Shown after a successful retract.
 * @returns {React.ReactElement|null}
 */
export const RetractDialog = ({
  open,
  funnel = null,
  onConfirm,
  onCancel,
  retracting = false,
  note = null,
}) => {
  const dialogRef = useRef(null);

  // Focus the dialog itself, not a button. The body is the reason this screen
  // exists, and landing on "Retract" invites confirming without reading it.
  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const counts = splitRetractCounts(funnel);
  const isDone = Boolean(note);

  const handleOverlayClick = (event) => {
    // Only a click on the backdrop itself. Without this, a drag that starts
    // inside the dialog and ends on its edge dismisses the confirm step.
    if (event.target === event.currentTarget) onCancel?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") onCancel?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        className="w-full max-w-sm rounded-[10px] border border-bc-border bg-white p-5 focus-visible:outline-none"
      >
        <h2 id={TITLE_ID} className="text-base font-semibold text-bc-text">
          {isDone ? "Broadcast retracted" : "Retract this broadcast?"}
        </h2>

        <div className="mt-2 flex flex-col gap-1 text-[13px] text-bc-text-2">
          {isDone ? (
            // VERBATIM. This is the server describing what already happened;
            // rewriting a statement of fact into friendlier words is how a
            // console ends up reassuring someone about an outcome it did not
            // actually get.
            <p>{note}</p>
          ) : counts ? (
            <p>
              This stops delivery to the <Num value={counts.notYetSent} /> people
              not yet sent to. The <Num value={counts.alreadySent} /> already
              sent cannot be recalled.
            </p>
          ) : (
            <p>{FALLBACK_COPY}</p>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          {isDone ? (
            <button
              type="button"
              onClick={onCancel}
              className={`${BUTTON_BASE} ${GHOST_BUTTON}`}
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                disabled={retracting}
                className={`${BUTTON_BASE} ${GHOST_BUTTON} ${
                  retracting ? DISABLED_BUTTON : ""
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={retracting}
                className={`${BUTTON_BASE} ${DANGER_BUTTON} ${
                  retracting ? DISABLED_BUTTON : ""
                }`}
              >
                {retracting ? "Retracting…" : "Retract"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetractDialog;
